import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Bell, Loader2, ArrowRight, Calendar, MessageCircle, AlertCircle, CheckCircle2, Shield, Megaphone } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { AppNotification } from "@/hooks/useNotificationsData";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  notification: AppNotification | null;
}

const TYPE_VISUAL: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  booking_accepted: { icon: <CheckCircle2 className="w-4 h-4" />, color: "text-green-600", label: "Réservation acceptée" },
  booking_refused: { icon: <AlertCircle className="w-4 h-4" />, color: "text-destructive", label: "Réservation refusée" },
  booking_new: { icon: <Calendar className="w-4 h-4" />, color: "text-primary", label: "Nouvelle réservation" },
  booking_cancelled: { icon: <AlertCircle className="w-4 h-4" />, color: "text-destructive", label: "Réservation annulée" },
  booking_validated: { icon: <CheckCircle2 className="w-4 h-4" />, color: "text-green-600", label: "Réservation validée" },
  booking_in_progress: { icon: <Calendar className="w-4 h-4" />, color: "text-primary", label: "Prestation démarrée" },
  booking_completed: { icon: <CheckCircle2 className="w-4 h-4" />, color: "text-primary", label: "Prestation terminée" },
  care_request_new: { icon: <Megaphone className="w-4 h-4" />, color: "text-primary", label: "Annonce publique — pas un message privé" },
  care_request_response: { icon: <Megaphone className="w-4 h-4" />, color: "text-primary", label: "Réponse à votre annonce" },
  new_message: { icon: <MessageCircle className="w-4 h-4" />, color: "text-blue-600", label: "Message privé" },
  admin_message: { icon: <Shield className="w-4 h-4" />, color: "text-primary", label: "Message administrateur" },
  admin_warning: { icon: <AlertCircle className="w-4 h-4" />, color: "text-destructive", label: "Avertissement administration" },
  account_suspended: { icon: <AlertCircle className="w-4 h-4" />, color: "text-destructive", label: "Compte suspendu" },
  account_reactivated: { icon: <CheckCircle2 className="w-4 h-4" />, color: "text-green-600", label: "Compte réactivé" },
  payment_released: { icon: <CheckCircle2 className="w-4 h-4" />, color: "text-green-600", label: "Paiement débloqué" },
  payout_requested: { icon: <Bell className="w-4 h-4" />, color: "text-primary", label: "Demande de virement" },
  payout_approved: { icon: <CheckCircle2 className="w-4 h-4" />, color: "text-green-600", label: "Virement approuvé" },
  payout_paid: { icon: <CheckCircle2 className="w-4 h-4" />, color: "text-green-600", label: "Virement effectué" },
  payout_rejected: { icon: <AlertCircle className="w-4 h-4" />, color: "text-destructive", label: "Virement refusé" },
  dispute_opened: { icon: <AlertCircle className="w-4 h-4" />, color: "text-destructive", label: "Litige ouvert" },
  dispute_update: { icon: <AlertCircle className="w-4 h-4" />, color: "text-primary", label: "Mise à jour du litige" },
  contact_attempt: { icon: <AlertCircle className="w-4 h-4" />, color: "text-destructive", label: "Tentative de contact détectée" },
};

const resolveRoute = (notif: AppNotification): string | null => {
  const meta = (notif.metadata ?? {}) as Record<string, unknown>;
  const bookingId = typeof meta.booking_id === "string" ? meta.booking_id : null;
  const conversationId = typeof meta.conversation_id === "string" ? meta.conversation_id : null;
  const requestId = typeof meta.request_id === "string" ? meta.request_id : null;
  if (notif.type.startsWith("booking_") && bookingId) return `/mes-reservations?booking=${bookingId}`;
  if ((notif.type === "new_message" || notif.type === "admin_message") && conversationId) return `/messages?conversation=${conversationId}`;
  if (notif.type === "new_message") return "/messages";
  if (notif.type.startsWith("care_request")) return requestId ? `/demandes?request=${requestId}` : "/demandes";
  if (["payout_requested","payout_approved","payout_paid","payout_rejected","payment_released"].includes(notif.type)) return "/mes-gains";
  if (notif.type === "account_suspended" || notif.type === "account_reactivated") return "/settings";
  if (notif.type === "dispute_opened" || notif.type === "dispute_update") return "/messages";
  if (notif.type === "admin_warning" && conversationId) return `/messages?conversation=${conversationId}`;
  return null;
};

const NotificationDetailDialog = ({ open, onOpenChange, notification }: Props) => {
  const navigate = useNavigate();
  const [adminContent, setAdminContent] = useState<string | null>(null);
  const [loadingAdmin, setLoadingAdmin] = useState(false);

  useEffect(() => {
    if (!open || !notification) { setAdminContent(null); return; }
    if (notification.type !== "admin_message") return;
    const meta = (notification.metadata ?? {}) as Record<string, unknown>;
    const adminDmId = typeof meta.admin_dm_id === "string" ? meta.admin_dm_id : null;
    if (!adminDmId) return;
    setLoadingAdmin(true);
    supabase.from("admin_direct_messages").select("content").eq("id", adminDmId).maybeSingle().then(({ data }) => {
      if (data?.content) setAdminContent(data.content);
      setLoadingAdmin(false);
    });
  }, [open, notification]);

  if (!notification) return null;
  const visual = TYPE_VISUAL[notification.type] ?? { icon: <Bell className="w-4 h-4" />, color: "text-muted-foreground", label: "Notification" };
  const route = resolveRoute(notification);
  const isCareAnnouncement = notification.type === "care_request_new";
  const fullContent = isCareAnnouncement
    ? `Un parent a publié une annonce de garde sur Click&Gard.\n\n⚠️ Ceci n’est pas un message privé : ce parent ne vous a pas contacté personnellement. Consultez l’annonce uniquement si vous souhaitez proposer vos services.${notification.body ? `\n\n${notification.body}` : ""}`
    : notification.type === "admin_message" && adminContent ? adminContent : notification.body;
  const displayedTitle = isCareAnnouncement ? "📢 Nouvelle annonce de garde" : notification.title;
  const dateLabel = new Date(notification.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-left">
            <div className={`w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 ${visual.color}`}>{visual.icon}</div>
            <span className="flex-1 min-w-0">{displayedTitle}</span>
          </DialogTitle>
          <DialogDescription className="sr-only">{visual.label}</DialogDescription>
        </DialogHeader>
        {isCareAnnouncement && <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm font-semibold text-primary">📢 Annonce publique — pas un message privé</div>}
        <div className="bg-muted/40 border border-border rounded-xl p-4 max-h-[50vh] overflow-y-auto">
          {loadingAdmin ? <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div> : <p className="whitespace-pre-wrap text-sm text-foreground">{fullContent || "Aucun détail supplémentaire pour cette notification."}</p>}
          <p className="text-[11px] text-muted-foreground mt-3">{visual.label} · {dateLabel}</p>
        </div>
        <div className="flex gap-2 justify-end flex-wrap">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fermer</Button>
          {route && <Button onClick={() => { onOpenChange(false); navigate(route); }} className="gap-1.5">
            {isCareAnnouncement ? "Voir l’annonce" : notification.type === "new_message" ? "Ouvrir la messagerie" : "Voir le détail"}<ArrowRight className="w-4 h-4" />
          </Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NotificationDetailDialog;
